import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import WorkspaceNavigation, { Brand } from "./WorkspaceNavigation";
import SettingsButton from "./SettingsButton";
import DebateThreadView from "./DebateThreadView";
import { expertLabel } from "./FeedPost";
import { FEED_ENABLED } from "../utils/features";
import { loadSharedDebate, SharedDebate } from "../firebase/share";

/** A debate opened from a private share link. */
export default function SharedDebateView() {
  const { shareId = "" } = useParams();
  const navigate = useNavigate();
  const [debate, setDebate] = useState<SharedDebate | null | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setDebate(undefined);
    setFailed(false);
    loadSharedDebate(shareId)
      .then(setDebate)
      .catch(() => setFailed(true));
  }, [shareId]);

  const panel = debate
    ? debate.debateMode === "single" ? "Deep analysis" : debate.experts.map(expertLabel).join(", ")
    : "";

  return (
    <div className="app-frame is-page-scroll">
      <a href="#shared-debate" className="skip-link">Skip to debate</a>
      <header className="workspace-header">
        <Brand />
        <WorkspaceNavigation
          onChange={(mode) =>
            navigate(mode === "video" ? "/video" : mode === "chat" ? "/?mode=chat" : mode === "feed" ? "/feed" : "/")
          }
        />
        <div className="header-actions"><SettingsButton /></div>
      </header>
      <main id="shared-debate" className="flex-1 overflow-x-clip">
        <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 pt-4 pb-16">
          {debate === undefined && !failed && <p className="text-sm text-muted" role="status">Loading debate…</p>}
          {failed && (
            <p role="alert" className="text-sm text-danger">Couldn't load this debate. Check your connection and reload the page.</p>
          )}
          {debate === null && (
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-content">This link doesn't work anymore</h1>
              <p className="mt-1 text-sm text-muted">The person who shared it turned it off, or the address is incomplete.</p>
              {FEED_ENABLED && (
                <Link to="/feed" className="mt-3 inline-block text-sm font-medium text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus rounded-sm">
                  Browse public debates
                </Link>
              )}
            </div>
          )}
          {debate && (
            <>
              <div className="flex items-center gap-2 flex-wrap pb-4 mb-4 border-b border-line text-sm text-muted">
                <span>Shared by <span className="font-medium text-content">{debate.authorName}</span></span>
                <span aria-hidden="true">·</span>
                <span>{panel}</span>
                <Link
                  to="/"
                  className="ml-auto px-3 py-1.5 rounded-md border border-line bg-surface text-sm font-medium text-content hover:bg-subtle active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-focus transition-colors duration-150"
                >
                  Start your own debate
                </Link>
              </div>
              <h1 className="sr-only">{debate.question}</h1>
              <DebateThreadView
                debateTopic={debate.question}
                councilProgress={debate.steps}
                chaosProgress={debate.chaosSteps}
                councilFinal={debate.finalAggregate}
                facilitatorDone
                isLoading={false}
                selectedExperts={debate.experts}
                debateMode={debate.debateMode}
                askerName={debate.authorName}
                verdict={debate.verdict}
                sources={debate.sources}
              />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
